/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  RequestGuestChargingStationInterFace,
  GuestChargingStationInitialProperties,
  GuestChargingStationResponseInterface,
} from '../../../interface/partner-interfaces/guest-charging-location-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import stationManagementApi from '../../../utils/api/partner/guest-charging';
// a thunk to fetch users
export const fetchGuestChargingStation = createAsyncThunk<
  GuestChargingStationResponseInterface,
  RequestGuestChargingStationInterFace
>(
  'reports-summary/fetchGuestChargingStation',
  async (data: RequestGuestChargingStationInterFace, { dispatch }) => {
    try {
      const response = await stationManagementApi.fetchGuestChargingList(
        removeEmptyKeys({ ...data }),
      );
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListGuestChargingStationFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingStation: string }
>(
  'reports-summary/fetchListGuestChargingStationFilters',
  async ({ chargingStation }, { dispatch }) => {
    try {
      const response = await stationManagementApi.fetchGuestChargingListFilter({ chargingStation });
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: GuestChargingStationInitialProperties = {
  GuestChargingStation: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const GuestChargingStationSlice = createSlice({
  name: 'reports-summary',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateGuestChargingStationFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state.
     */
    disableGuestChargingStationFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchGuestChargingStation.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGuestChargingStation.fulfilled, (state, action) => {
        state.loading = false;
        state.GuestChargingStation = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchGuestChargingStation.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListGuestChargingStationFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListGuestChargingStationFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload];
        },
      )
      .addCase(fetchListGuestChargingStationFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
// Export the actions
export const {
  updateGuestChargingStationFilters,
  disableGuestChargingStationFiltersApplied,
  setSortData,
} = GuestChargingStationSlice.actions;

// Export the reducer
export default GuestChargingStationSlice.reducer;

export const disableGuestChargingLocationFiltersApplied = disableGuestChargingStationFiltersApplied;
export const fetchListGuestChargingLocationFilters = fetchListGuestChargingStationFilters;
export const updateGuestChargingLocationFilters = updateGuestChargingStationFilters;
